import { Link } from "react-router-dom";
import {
    Check,
    X,
    ArrowRight,
    ArrowDown,
    Sparkles,
    User,
    Users,
    Heart,
    Landmark,
} from "lucide-react";
import { Reveal, LazyImage } from "../components/primitives";
import NavBar from "../components/NavBar";
import { IMG } from "../lib/api";

/* --------------------------- Hero --------------------------- */
function Hero() {
    return (
        <section className="relative overflow-hidden pt-32 pb-20 md:pt-44 md:pb-28" data-testid="cgpt-section-hero">
            <div className="pointer-events-none absolute inset-0 -z-10">
                <LazyImage src={IMG("hero_couple")} alt="" aspect="absolute inset-0 w-full h-full" priority />
                <div className="absolute inset-0 bg-ivory/92" />
            </div>
            <div className="mx-auto max-w-4xl px-6 text-center md:px-12">
                <Reveal>
                    <span className="eyebrow" data-testid="cgpt-hero-eyebrow">The question everyone asks</span>
                </Reveal>
                <Reveal delay={80}>
                    <h1
                        className="mt-6 font-serif text-[38px] leading-[1.06] tracking-tight text-charcoal sm:text-5xl md:text-[64px] md:leading-[1.04] text-balance"
                        data-testid="cgpt-hero-headline"
                    >
                        Can't ChatGPT do this for free?
                    </h1>
                </Reveal>
                <Reveal delay={160}>
                    <p className="mx-auto mt-8 max-w-xl font-serif text-2xl italic leading-snug text-charcoal/80">
                        Yes. And that's exactly why Vowze exists.
                    </p>
                </Reveal>
                <Reveal delay={240}>
                    <p className="mx-auto mt-6 max-w-2xl text-[17px] leading-relaxed text-ink" data-testid="cgpt-hero-sub">
                        ChatGPT can analyze your photos, suggest colours, recommend outfits and even generate images.
                        But turning all of that into one coordinated wedding vision is a different problem.
                    </p>
                </Reveal>
                <Reveal delay={320}>
                    <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                        <Link to="/questionnaire/couple" className="btn-gold" data-testid="cgpt-hero-cta-primary">
                            Try Vowze — ₹399 / event
                            <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                        <a href="#difference" className="btn-ghost" data-testid="cgpt-hero-cta-secondary">
                            See how it's different
                            <ArrowDown className="ml-2 h-4 w-4" />
                        </a>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}

/* --------------------------- Section 1 — Honest answer --------------------------- */
function HonestAnswer() {
    const items = [
        "You can upload your photos.",
        "You can ask what colours suit you.",
        "You can ask what your partner should wear.",
        "You can ask for outfit ideas.",
        "You can generate wedding images.",
    ];
    return (
        <section className="py-24 md:py-32" data-testid="cgpt-section-honest">
            <div className="mx-auto max-w-3xl px-6 text-center md:px-12">
                <Reveal>
                    <span className="eyebrow">The honest answer</span>
                    <h2 className="mt-6 font-serif text-4xl leading-tight text-charcoal md:text-5xl text-balance">
                        Yes. ChatGPT can do a lot of this.
                    </h2>
                </Reveal>
                <Reveal delay={120}>
                    <ul className="mx-auto mt-12 max-w-md space-y-4 text-left">
                        {items.map((it) => (
                            <li key={it} className="flex items-start gap-3 text-[16px] text-charcoal/85">
                                <Check className="mt-0.5 h-4 w-4 flex-none text-gold" />
                                <span>{it}</span>
                            </li>
                        ))}
                    </ul>
                </Reveal>
                <Reveal delay={220}>
                    <p className="mt-12 text-[17px] leading-relaxed text-ink">
                        We're not going to pretend otherwise. ChatGPT is incredibly useful.
                        <br className="hidden md:block" />
                        The difference is what happens <em className="text-charcoal">after</em> the answers.
                    </p>
                </Reveal>
            </div>
        </section>
    );
}

/* --------------------------- Section 2 — The real problem --------------------------- */
function RealProblem() {
    const qs = [
        "What suits you?",
        "What suits your partner?",
        "What works for your wedding tradition?",
        "What works for this particular event?",
        "Do the bride and groom actually look coordinated?",
        "Does the decor work with the outfits?",
    ];
    return (
        <section className="bg-champagne/40 py-24 md:py-32" data-testid="cgpt-section-problem">
            <div className="mx-auto max-w-4xl px-6 text-center md:px-12">
                <Reveal>
                    <span className="eyebrow">The real problem</span>
                    <h2 className="mt-6 font-serif text-3xl leading-tight text-charcoal md:text-[44px] md:leading-[1.1]">
                        The hard part isn't getting an answer.
                    </h2>
                    <p className="mt-4 font-serif text-4xl italic leading-tight text-gold md:text-[56px]">
                        The hard part is knowing what to do with it.
                    </p>
                </Reveal>
                <Reveal delay={140}>
                    <p className="mx-auto mt-10 max-w-2xl text-[17px] leading-relaxed text-ink">
                        Wedding styling isn't one question. It's dozens of decisions that need to work together.
                    </p>
                    <div className="mx-auto mt-10 grid max-w-2xl gap-3 text-left sm:grid-cols-2">
                        {qs.map((q) => (
                            <div key={q} className="rounded-2xl bg-white/70 px-5 py-4 text-[14px] text-charcoal/85 shadow-soft">
                                {q}
                            </div>
                        ))}
                    </div>
                </Reveal>
                <Reveal delay={220}>
                    <p className="mt-12 font-serif text-2xl italic text-charcoal/80">
                        Will the whole thing feel elegant, traditional, modern — or completely mismatched?
                    </p>
                    <p className="mt-4 text-sm uppercase tracking-[0.25em] text-gold">That's where Vowze comes in.</p>
                </Reveal>
            </div>
        </section>
    );
}

/* --------------------------- Section 3 — ChatGPT vs Vowze --------------------------- */
function Comparison() {
    const chatgpt = [
        "You decide what to ask",
        "You write the prompts",
        "You provide the context",
        "You ask follow-up questions",
        "You generate multiple versions",
        "You compare the answers yourself",
        "You coordinate bride + groom yourself",
        "You figure out which ideas belong together",
    ];
    const vowze = [
        "Guided wedding-specific process",
        "Bride + groom analysed together",
        "Wedding style is part of the decision",
        "Event-specific recommendations",
        "Coordinated bride + groom styling",
        "Decor & background considered",
        "One visual direction",
        "Built specifically for wedding decisions",
    ];
    return (
        <section id="difference" className="py-24 md:py-32" data-testid="cgpt-section-comparison">
            <div className="mx-auto max-w-6xl px-6 md:px-12">
                <Reveal className="mx-auto max-w-2xl text-center">
                    <span className="eyebrow">Side by side</span>
                    <h2 className="mt-6 font-serif text-4xl leading-tight text-charcoal md:text-5xl">
                        ChatGPT vs Vowze
                    </h2>
                </Reveal>
                <div className="mt-14 grid gap-6 md:grid-cols-2 md:gap-8">
                    <Reveal>
                        <div className="h-full rounded-[28px] bg-white p-8 shadow-soft md:p-10" data-testid="cgpt-comparison-chatgpt">
                            <div className="eyebrow">ChatGPT</div>
                            <ul className="mt-6 space-y-3.5">
                                {chatgpt.map((t) => (
                                    <li key={t} className="flex items-start gap-3 text-[14px] text-charcoal/75">
                                        <X className="mt-0.5 h-4 w-4 flex-none text-charcoal/30" />
                                        <span>{t}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Reveal>
                    <Reveal delay={120}>
                        <div className="h-full rounded-[28px] bg-charcoal p-8 text-ivory shadow-soft ring-1 ring-gold/50 md:p-10" data-testid="cgpt-comparison-vowze">
                            <div className="text-[11px] uppercase tracking-[0.25em] text-gold">Vowze</div>
                            <ul className="mt-6 space-y-3.5">
                                {vowze.map((t) => (
                                    <li key={t} className="flex items-start gap-3 text-[14px] text-ivory/90">
                                        <Check className="mt-0.5 h-4 w-4 flex-none text-gold" />
                                        <span>{t}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Reveal>
                </div>
                <Reveal delay={220}>
                    <p className="mt-14 text-center font-serif text-2xl leading-snug text-charcoal md:text-3xl">
                        ChatGPT gives you answers. <span className="italic text-gold">Vowze gives you a system.</span>
                    </p>
                </Reveal>
            </div>
        </section>
    );
}

/* --------------------------- Section 4 — Wedding style --------------------------- */
function WeddingStyle() {
    const styles = [
        { name: "South Indian", img: "https://images.unsplash.com/photo-1617627143750-d86bc21e42bb?crop=entropy&cs=srgb&fm=jpg&q=80&w=800" },
        { name: "North Indian", img: "https://images.unsplash.com/photo-1746372283841-dbb3838f9935?crop=entropy&cs=srgb&fm=jpg&q=80&w=800" },
        { name: "Christian", img: "https://images.unsplash.com/photo-1664853444491-feedf1695367?crop=entropy&cs=srgb&fm=jpg&q=80&w=800" },
        { name: "Muslim", img: "https://images.pexels.com/photos/36231699/pexels-photo-36231699.jpeg?auto=compress&cs=tinysrgb&h=800" },
        { name: "Sikh / Punjabi", img: "https://images.unsplash.com/photo-1633104502646-eb5ea88b3190?crop=entropy&cs=srgb&fm=jpg&q=80&w=800" },
        { name: "Modern / Contemporary", img: "https://images.pexels.com/photos/38551463/pexels-photo-38551463.jpeg?auto=compress&cs=tinysrgb&h=800" },
        { name: "Fusion", img: "https://images.pexels.com/photos/10238137/pexels-photo-10238137.jpeg?auto=compress&cs=tinysrgb&h=800" },
    ];
    return (
        <section className="bg-champagne/40 py-24 md:py-32" data-testid="cgpt-section-style">
            <div className="mx-auto max-w-7xl px-6 md:px-12">
                <Reveal className="mx-auto max-w-2xl text-center">
                    <span className="eyebrow">Your wedding style</span>
                    <h2 className="mt-6 font-serif text-4xl leading-tight text-charcoal md:text-5xl text-balance">
                        Your wedding isn't generic.
                    </h2>
                    <p className="mt-2 font-serif text-2xl italic text-gold">Why should your style be?</p>
                    <p className="mt-6 text-[16px] leading-relaxed text-ink">
                        A colour that looks beautiful on you doesn't automatically make sense for your wedding.
                        A styling recommendation needs context.
                    </p>
                </Reveal>
                <Reveal delay={100}>
                    <div className="mx-auto mt-10 max-w-3xl space-y-3">
                        {[["Your photos", "Your partner", "Your wedding style", "Your event"], ["Your preference"]].map((row, ri) => (
                            <div key={ri} className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-center font-serif text-sm text-charcoal/70 md:text-base">
                                {row.map((t, i) => (
                                    <span key={t} className="flex items-center gap-3">
                                        {(ri > 0 || i > 0) && <span className="text-gold">+</span>}
                                        <span className="rounded-full bg-white px-4 py-1.5 shadow-soft">{t}</span>
                                    </span>
                                ))}
                            </div>
                        ))}
                    </div>
                    <p className="mt-6 text-center text-sm text-ink">
                        Then turns those inputs into one coordinated visual direction.
                    </p>
                </Reveal>
                <div className="mt-14 grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-5 lg:grid-cols-7">
                    {styles.map((s, i) => (
                        <Reveal key={s.name} delay={i * 60}>
                            <div className="group overflow-hidden rounded-[20px] shadow-soft" data-testid={`cgpt-style-card-${i}`}>
                                <div className="relative aspect-[3/4] overflow-hidden">
                                    <img
                                        src={s.img}
                                        alt={s.name}
                                        className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 via-transparent to-transparent" />
                                    <span className="absolute bottom-3 left-0 right-0 px-2 text-center text-[11px] font-medium uppercase tracking-wider text-ivory">
                                        {s.name}
                                    </span>
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* --------------------------- Section 5 — Couple coordination --------------------------- */
function CoupleCoordination() {
    return (
        <section className="py-24 md:py-32" data-testid="cgpt-section-coordination">
            <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 md:grid-cols-12 md:px-12">
                <Reveal className="md:col-span-7 md:order-2">
                    <span className="eyebrow">It's rarely a solo decision</span>
                    <h2 className="mt-6 font-serif text-3xl leading-tight text-charcoal md:text-[46px] md:leading-[1.1] text-balance">
                        Looks good on me <span className="text-gold">≠</span> looks good on us.
                    </h2>
                    <p className="mt-6 text-[17px] leading-relaxed text-ink">
                        Your outfit has to work with your partner's. Your colours need to complement each other.
                        Your jewellery, accessories, flowers, decor and environment need to feel like they belong to the same wedding.
                    </p>
                    <div className="mt-8 space-y-3">
                        <p className="text-sm text-charcoal/60">Vowze doesn't just ask:</p>
                        <p className="rounded-2xl bg-champagne/60 px-5 py-3 text-[15px] italic text-charcoal/70">"What looks good on you?"</p>
                        <p className="text-sm text-charcoal/60">It asks:</p>
                        <p className="rounded-2xl bg-charcoal px-5 py-3 text-[15px] font-medium italic text-gold">"What looks good together?"</p>
                    </div>
                </Reveal>
                <Reveal delay={140} className="md:col-span-5 md:order-1">
                    <div className="overflow-hidden rounded-[28px] shadow-soft">
                        <LazyImage src={IMG("couple_coordination")} alt="Couple in coordinated wedding attire" aspect="aspect-[4/5]" />
                    </div>
                </Reveal>
            </div>
        </section>
    );
}

/* --------------------------- Section 6 — One event --------------------------- */
function OneEvent() {
    const cards = [
        { k: "01", icon: User, title: "Bride", sub: "A personalized bride styling visualization." },
        { k: "02", icon: Users, title: "Groom", sub: "A coordinated groom styling visualization." },
        { k: "03", icon: Heart, title: "Couple", sub: "Both together, showing how the styling works as a pair." },
        { k: "04", icon: Landmark, title: "Wedding environment", sub: "A visual direction for the decor / background / environment." },
    ];
    return (
        <section className="bg-champagne/40 py-24 md:py-32" data-testid="cgpt-section-oneevent">
            <div className="mx-auto max-w-7xl px-6 md:px-12">
                <Reveal className="mx-auto max-w-2xl text-center">
                    <span className="eyebrow">For one selected event</span>
                    <h2 className="mt-6 font-serif text-4xl leading-tight text-charcoal md:text-5xl text-balance">
                        Not four random AI images.
                    </h2>
                    <p className="mt-2 font-serif text-2xl italic text-gold">One coordinated wedding direction.</p>
                </Reveal>
                <div className="mt-14 grid gap-6 md:grid-cols-4 md:gap-5">
                    {cards.map((c, i) => {
                        const Icon = c.icon;
                        return (
                            <Reveal key={c.k} delay={i * 90}>
                                <div className="h-full rounded-[24px] bg-white p-7 shadow-soft transition hover:-translate-y-1" data-testid={`cgpt-event-card-${i}`}>
                                    <div className="flex items-center justify-between">
                                        <span className="font-serif text-sm text-gold">{c.k}</span>
                                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-champagne text-charcoal">
                                            <Icon className="h-4 w-4" />
                                        </span>
                                    </div>
                                    <h3 className="mt-6 font-serif text-xl leading-snug text-charcoal">{c.title}</h3>
                                    <p className="mt-3 text-sm leading-relaxed text-ink">{c.sub}</p>
                                </div>
                            </Reveal>
                        );
                    })}
                </div>
                <Reveal delay={200}>
                    <p className="mt-14 text-center font-serif text-2xl leading-snug text-charcoal md:text-3xl text-balance">
                        The point isn't to generate pretty pictures.
                        <br />
                        <span className="italic text-gold">The point is to help you make a better wedding decision.</span>
                    </p>
                </Reveal>
            </div>
        </section>
    );
}

/* --------------------------- Section 7 — Why 399 --------------------------- */
function WhyPrice() {
    const rows = [
        ["Personal colour analysis", "Wedding style context", "Event-specific styling"],
        ["Bride + groom coordination", "Decor direction", "Visualization"],
        ["A coherent wedding vision"],
    ];
    return (
        <section className="py-24 md:py-32" data-testid="cgpt-section-price">
            <div className="mx-auto max-w-4xl px-6 text-center md:px-12">
                <Reveal>
                    <span className="eyebrow">The honest math</span>
                    <h2 className="mt-6 font-serif text-4xl leading-tight text-charcoal md:text-5xl">
                        So… why pay ₹399?
                    </h2>
                    <p className="mt-6 text-[17px] leading-relaxed text-ink">
                        You're not paying ₹399 because AI images are impossible to make elsewhere.
                        You're paying for the work <em className="text-charcoal">between</em> the pixels.
                    </p>
                </Reveal>
                <Reveal delay={120}>
                    <div className="mx-auto mt-10 max-w-2xl space-y-3">
                        {rows.map((row, ri) => (
                            <div key={ri} className="flex flex-wrap items-center justify-center gap-2">
                                {row.map((l, i) => (
                                    <span key={l} className="flex items-center gap-2">
                                        <span
                                            className={`rounded-full px-4 py-2 text-[13px] ${
                                                ri === rows.length - 1 ? "bg-gold text-charcoal font-medium" : "bg-charcoal text-ivory"
                                            }`}
                                        >
                                            {l}
                                        </span>
                                        {i < row.length - 1 && <ArrowRight className="h-3.5 w-3.5 flex-none text-gold" />}
                                    </span>
                                ))}
                            </div>
                        ))}
                    </div>
                </Reveal>
                <Reveal delay={220}>
                    <div className="mx-auto mt-12 grid max-w-2xl gap-4 text-left sm:grid-cols-3">
                        {[
                            "One wedding outfit can cost thousands.",
                            "Jewellery can cost tens of thousands.",
                            "Decor can cost much more.",
                        ].map((t) => (
                            <div key={t} className="rounded-2xl bg-champagne/50 p-5 text-sm text-charcoal/80">{t}</div>
                        ))}
                    </div>
                    <p className="mt-8 text-[16px] italic text-charcoal/75">
                        A wrong colour, outfit or styling decision can cost far more than ₹399.
                    </p>
                    <p className="mt-3 text-sm text-ink">Vowze is a small clarity step before you commit.</p>
                </Reveal>
                <Reveal delay={300}>
                    <p className="mt-14 font-serif text-3xl leading-tight text-charcoal md:text-[44px] text-balance">
                        Your wedding is too expensive to plan by <span className="italic text-gold">guesswork.</span>
                    </p>
                </Reveal>
            </div>
        </section>
    );
}

/* --------------------------- Section 8 — We like ChatGPT --------------------------- */
function LikeChatGPT() {
    return (
        <section className="bg-charcoal py-24 text-ivory md:py-32" data-testid="cgpt-section-like">
            <div className="mx-auto max-w-3xl px-6 text-center md:px-12">
                <Reveal>
                    <span className="eyebrow">No hard feelings</span>
                    <h2 className="mt-6 font-serif text-4xl leading-tight md:text-5xl">We actually like ChatGPT.</h2>
                </Reveal>
                <Reveal delay={100}>
                    <p className="mx-auto mt-8 max-w-md text-[17px] leading-relaxed text-ivory/80">
                        Use ChatGPT. Brainstorm. Learn. Explore. Ask questions. Get ideas.
                    </p>
                    <p className="mx-auto mt-6 max-w-md text-[17px] leading-relaxed text-ivory/80">
                        But when you're tired of exploring and ready to decide what <em>your</em> wedding should actually look like — that's where Vowze comes in.
                    </p>
                </Reveal>
                <Reveal delay={200}>
                    <p className="mt-12 font-serif text-2xl italic leading-snug text-gold md:text-3xl text-balance">
                        When you're ready to stop exploring and start deciding.
                    </p>
                </Reveal>
            </div>
        </section>
    );
}

/* --------------------------- Section 9 — Visual demo --------------------------- */
function VisualDemo() {
    return (
        <section className="py-24 md:py-32" data-testid="cgpt-section-demo">
            <div className="mx-auto max-w-6xl px-6 md:px-12">
                <Reveal className="mx-auto max-w-2xl text-center">
                    <span className="eyebrow">Same question, very different experience</span>
                    <h2 className="mt-6 font-serif text-4xl leading-tight text-charcoal md:text-5xl text-balance">
                        "What should I wear for my wedding?"
                    </h2>
                </Reveal>
                <div className="mt-14 grid gap-6 md:grid-cols-2 md:gap-8">
                    <Reveal>
                        <div className="h-full rounded-[28px] bg-white p-8 shadow-soft md:p-10">
                            <div className="eyebrow">ChatGPT</div>
                            <p className="mt-5 text-[15px] italic leading-relaxed text-charcoal/75">
                                "Try jewel tones, complementary colours, statement jewellery and a coordinated outfit for your partner…"
                            </p>
                            <div className="mt-8 space-y-3 border-t border-charcoal/10 pt-6">
                                {["More questions", "More prompts", "More context", "More comparisons"].map((t) => (
                                    <div key={t} className="flex items-center gap-3 text-[13px] text-charcoal/60">
                                        <ArrowDown className="h-3.5 w-3.5 text-charcoal/30" /> {t}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Reveal>
                    <Reveal delay={120}>
                        <div className="h-full rounded-[28px] bg-charcoal p-8 text-ivory shadow-soft ring-1 ring-gold/50 md:p-10">
                            <div className="text-[11px] uppercase tracking-[0.25em] text-gold">Vowze</div>
                            <div className="mt-5 flex flex-wrap gap-2 text-[13px]">
                                {["Bride photo", "Groom photo", "Wedding style", "Event"].map((t) => (
                                    <span key={t} className="rounded-full bg-ivory/10 px-3.5 py-1.5">{t}</span>
                                ))}
                            </div>
                            <ArrowDown className="mt-5 h-4 w-4 text-gold" />
                            <div className="mt-3 flex flex-wrap gap-2 text-[13px]">
                                {["Colour direction", "Bride styling", "Groom styling", "Couple coordination", "Wedding environment"].map((t) => (
                                    <span key={t} className="rounded-full bg-gold/15 px-3.5 py-1.5 text-gold">{t}</span>
                                ))}
                            </div>
                            <ArrowDown className="mt-5 h-4 w-4 text-gold" />
                            <p className="mt-3 font-serif text-2xl text-ivory">4 coordinated visuals.</p>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}

/* --------------------------- Section 10 — Final CTA --------------------------- */
function FinalCTA() {
    return (
        <section className="relative overflow-hidden bg-champagne/40 py-28 md:py-36" data-testid="cgpt-section-finalcta">
            <div className="mx-auto max-w-4xl px-6 text-center md:px-12">
                <Reveal>
                    <span className="eyebrow">Decide, don't guess</span>
                    <h2 className="mt-6 font-serif text-3xl leading-tight text-charcoal md:text-[42px] text-balance">
                        Stop asking "what might look good?"
                    </h2>
                    <p className="mt-3 font-serif text-4xl italic leading-tight text-gold md:text-[56px] text-balance">
                        See what could look good on you.
                    </p>
                </Reveal>
                <Reveal delay={140}>
                    <p className="mx-auto mt-8 max-w-lg text-[16px] leading-relaxed text-ink">
                        Start with one event. Get your personalized wedding visual direction.
                    </p>
                    <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                        <Link to="/questionnaire/couple" className="btn-gold" data-testid="cgpt-final-cta">
                            Visualize my wedding — ₹399
                            <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                    </div>
                    <p className="mt-5 text-xs uppercase tracking-[0.2em] text-charcoal/50">
                        No subscription · No complicated questionnaire · One event · Four coordinated visuals
                    </p>
                </Reveal>
            </div>
        </section>
    );
}

/* --------------------------- Footer --------------------------- */
function Footer() {
    return (
        <footer className="border-t border-charcoal/10 bg-ivory py-10" data-testid="cgpt-footer">
            <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-4 px-6 text-sm text-ink/70 md:flex-row md:items-center md:px-12">
                <div className="font-serif text-charcoal">Vowze<span className="text-gold">.</span></div>
                <div className="flex flex-wrap items-center gap-6">
                    <a href="/terms" className="hover:text-charcoal" data-testid="cgpt-footer-terms">Terms</a>
                    <a href="/privacy" className="hover:text-charcoal" data-testid="cgpt-footer-privacy">Privacy</a>
                    <span>© {new Date().getFullYear()} Vowze · Built for real weddings</span>
                </div>
            </div>
        </footer>
    );
}

/* --------------------------- Page --------------------------- */
export default function ChatGPTVsVowze() {
    return (
        <div className="bg-ivory" data-testid="cgpt-vs-vowze-page">
            <NavBar />
            <Hero />
            <HonestAnswer />
            <RealProblem />
            <Comparison />
            <WeddingStyle />
            <CoupleCoordination />
            <OneEvent />
            <WhyPrice />
            <LikeChatGPT />
            <VisualDemo />
            <FinalCTA />
            <Footer />
        </div>
    );
}
